"use client";

import Link from "next/link";
import { BiteGenerationVideo, type BiteVideoPhase, type BiteGenerationKind } from "./bite-generation-video";
import { forwardRef, type AnchorHTMLAttributes, type ButtonHTMLAttributes, type CSSProperties, type InputHTMLAttributes, type ReactNode } from "react";
import { CircleHelp, Inbox, LoaderCircle } from "lucide-react";
import { cn } from "@/lib/utils";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";

type BiteButtonVariant = "primary" | "secondary" | "ghost" | "accent" | "inverse";
type BiteButtonSize = "sm" | "md" | "lg";

interface BiteButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: BiteButtonVariant;
  size?: BiteButtonSize;
}

interface BiteLinkButtonProps extends AnchorHTMLAttributes<HTMLAnchorElement> {
  href: string;
  navigation?: "client" | "document";
  variant?: BiteButtonVariant;
  size?: BiteButtonSize;
}

export const BiteButton = forwardRef<HTMLButtonElement, BiteButtonProps>(function BiteButton(
  { variant = "primary", size = "md", className, ...props },
  ref,
) {
  return <button ref={ref} className={cn("bite-button", `bite-button--${variant}`, `bite-button--${size}`, className)} {...props} />;
});

export const BiteLinkButton = forwardRef<HTMLAnchorElement, BiteLinkButtonProps>(function BiteLinkButton(
  { href, navigation = "client", variant = "primary", size = "md", className, ...props },
  ref,
) {
  const classes = cn("bite-button", `bite-button--${variant}`, `bite-button--${size}`, className);
  if (navigation === "document") return <a ref={ref} href={href} className={classes} {...props} />;
  return <Link ref={ref} href={href} className={classes} {...props} />;
});

export function BiteInput({ className, ...props }: InputHTMLAttributes<HTMLInputElement>) {
  return <input className={cn("bite-input", className)} {...props} />;
}

export function BiteBadge({ tone = "neutral", children }: { tone?: "neutral" | "risk"; children: ReactNode }) {
  return <span className={cn("bite-badge", `bite-badge--${tone}`)}>{children}</span>;
}

export function BiteCard({ children, className }: { children: ReactNode; className?: string }) {
  return <div className={cn("bite-card", className)}>{children}</div>;
}

export function BiteSeparator() {
  return <div className="bite-separator" role="separator" />;
}

export function BiteSkeleton({ width = "full" }: { width?: "short" | "medium" | "full" }) {
  return <span className={cn("bite-skeleton", `bite-skeleton--${width}`)} aria-hidden="true" />;
}

export function BiteSpinner({ label = "불러오는 중" }: { label?: string }) {
  return (
    <span className="bite-spinner" role="status">
      <LoaderCircle aria-hidden="true" />
      <span>{label}</span>
    </span>
  );
}

export interface BiteProgressSection {
  id: string;
  title: string;
  status: string;
}

export function BiteReportProgress({
  ticker,
  companyName,
  sections = [],
  mode = "report",
  complete = false,
  videoActive = true,
  videoPhase = "generation",
  generationKind = "existing",
  onVideoFinished,
  children,
  footer,
}: {
  ticker: string;
  companyName?: string;
  sections?: BiteProgressSection[];
  mode?: "route" | "report" | "example";
  complete?: boolean;
  videoActive?: boolean;
  videoPhase?: BiteVideoPhase;
  generationKind?: BiteGenerationKind;
  onVideoFinished?: () => void;
  children?: ReactNode;
  footer?: ReactNode;
}) {
  const completed = sections.filter((section) => section.status === "done").length;
  const active = sections.find((section) => section.status === "running" || section.status === "regenerating");
  const total = sections.length;
  const percent = complete || videoPhase === "ending" ? 100 : total > 0 ? Math.round((completed / total) * 100) : 0;
  const heading = mode === "route"
    ? `${ticker} 분석 화면으로 이동하고 있습니다`
    : videoPhase === "ending" ? `${companyName || ticker} 보고서가 완성됐습니다` : mode==='example' ? `${companyName || ticker} 예시 보고서를 준비하고 있습니다` : `${companyName || ticker} 보고서를 만들고 있습니다`;
  const detail = mode === "route"
    ? "종목을 확인한 뒤 보고서 생성 화면으로 이동합니다."
    : videoPhase === "ending" ? "보고서 생성이 완료됐습니다."
    : mode==='example' ? "실제 분석을 마친 예시 보고서를 불러옵니다."
    : active
      ? `${active.title} 내용을 분석하고 있습니다.`
      : completed > 0
        ? `${completed}개 분석을 마쳤습니다.`
        : "공시와 재무 자료를 모으고 있습니다.";

  if (mode === "route") return (
    <section className="bite-report-progress" role="status" aria-live="polite" aria-busy="true">
      <div className="bite-report-progress__heading">
        <LoaderCircle aria-hidden="true" />
        <div>
          <span>{ticker}</span>
          <h2>{heading}</h2>
          <p>{detail}</p>
        </div>
      </div>
      <div className="bite-report-progress__track" aria-hidden="true">
        <span style={{ width: `${percent}%` }} />
      </div>
      {total > 0 && <p className="bite-report-progress__count">전체 {total}개 분석 가운데 {completed}개를 마쳤습니다.</p>}
    </section>
  );

  return (
    <section
      className="bite-research-animation bite-research-animation--continuous"
      data-complete={complete}
      aria-label={`${ticker} 보고서 생성과 표지 공개`}
      style={{ "--bite-progress": `${percent}%` } as CSSProperties}
    >
      <div className="bite-research-animation__viewport">
        {complete ? children : videoActive ? <BiteGenerationVideo phase={videoPhase} generationKind={generationKind} onFinished={onVideoFinished} /> : null}
      </div>
      <div className="bite-research-animation__status" aria-live="polite" aria-busy={!complete}>
        {footer || <>
        <h2>{heading}</h2>
        <p className="bite-generation-detail">{detail}</p>
        <div className="bite-research-animation__track" role="progressbar" aria-label="보고서 생성 진행률" aria-valuemin={0} aria-valuemax={100} aria-valuenow={total || videoPhase === "ending" ? percent : undefined} aria-valuetext={detail}><span /></div>
        {videoPhase!=='ending'&&<Link className="bite-generation-return" href="/">다른 종목을 살펴보기</Link>}
        {videoPhase!=='ending'&&mode!=='example'&&<p className="bite-generation-detail">페이지를 이동해도 생성은 계속됩니다. 홈에서 다시 확인할 수 있습니다.</p>}
        </>}
      </div>
    </section>
  );
}

export function BiteEmptyState({
  title = "표시할 브리핑이 없습니다.",
  description = "종목을 검색하면 확인한 근거가 이곳에 나타납니다.",
}: {
  title?: string;
  description?: string;
} = {}) {
  return (
    <div className="bite-empty-state">
      <Inbox aria-hidden="true" />
      <strong>{title}</strong>
      <p>{description}</p>
    </div>
  );
}

export function BiteTooltipDemo() {
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <BiteButton variant="ghost" size="sm" aria-label="근거 강도 설명">
          <CircleHelp aria-hidden="true" />
          근거 강도
        </BiteButton>
      </TooltipTrigger>
      <TooltipContent className="bite-scope bite-tooltip">확인한 근거가 같은 방향을 가리키는 정도입니다.</TooltipContent>
    </Tooltip>
  );
}

export function BiteDialogDemo() {
  return (
    <Dialog>
      <DialogTrigger asChild>
        <BiteButton variant="secondary">브리핑 안내 열기</BiteButton>
      </DialogTrigger>
      <DialogContent className="bite-scope bite-light bite-dialog" data-bite-theme="light" showCloseButton={false}>
        <DialogHeader>
          <DialogTitle className="bite-dialog__title">종목 브리핑 안내</DialogTitle>
          <DialogDescription className="bite-dialog__description">
            Bite는 같은 종목과 같은 데이터에 모든 사용자에게 동일한 브리핑을 제공합니다.
          </DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <DialogClose asChild>
            <BiteButton size="lg">확인</BiteButton>
          </DialogClose>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function BiteReportPattern() {
  const scores = [
    ["재무건전성", "62"],
    ["화제성", "70"],
    ["기술경쟁력", "58"],
    ["기술적분석", "60"],
  ];

  return (
    <article className="bite-report-pattern" aria-label="Bite 종목 보고서 패턴">
      <div className="score">
        <div className="score-big">62<span className="u">/ 100</span></div>
        <div className="score-body">
          <h3>우수</h3>
          <p>AI가 공시와 재무와 뉴스 및 기술 지표를 종합해 산출한 점수입니다.</p>
          <div className="score-breaks">
            {scores.map(([label, score]) => (
              <div className="sb" key={label}>
                <div className="k">{label}</div>
                <div className="v">{score}<span className="u">/100</span></div>
                <div className="bar"><span /></div>
              </div>
            ))}
          </div>
        </div>
      </div>
      <section className="rpt-section">
        <div className="sec-kicker">
          <span className="n">01 / 06</span>
          <h2>재무 및 공시 분석</h2>
        </div>
        <div className="sec-body">
          <h3>실적이 달라진 이유를 수치와 함께 설명합니다.</h3>
          <p>긴 보고서는 섹션 간격과 채움 표면을 사용해 읽는 흐름을 분명하게 나눕니다.</p>
          <div className="kpi-grid">
            <div className="kpi"><div className="k">영업이익률</div><div className="v">1.4%</div></div>
            <div className="kpi"><div className="k">현금 흐름</div><div className="v">안정</div></div>
            <div className="kpi"><div className="k">부채 부담</div><div className="v">낮음</div></div>
            <div className="kpi"><div className="k">추세</div><div className="v">혼조</div></div>
          </div>
        </div>
      </section>
    </article>
  );
}
